import type { DehydratedState, VueQueryPluginOptions } from '@tanstack/vue-query'
import { VueQueryPlugin, QueryClient, hydrate, dehydrate, QueryCache } from '@tanstack/vue-query'
// Nuxt 3 app aliases
import { defineNuxtPlugin, useState } from '#imports'
import { isTransientApiError } from '~/utils/apiError'

type ErrorSource = { from?: 'api' | 'query' | 'mutation' }

function shouldThrowGlobally(error: unknown, source: 'query' | 'mutation'): boolean {
  if (isTransientApiError(error)) return false

  if (error && typeof error === 'object') {
    const errorWithSource = error as ErrorSource
    errorWithSource.from = source
  }

  return true
}

export default defineNuxtPlugin((nuxt) => {
  const vueQueryState = useState<DehydratedState | null>('vue-query')
  // Modify your Vue Query global settings here
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 100,
        refetchOnWindowFocus: false,
        retry(failureCount) {
          return failureCount < 0
        },
        throwOnError: (error) => shouldThrowGlobally(error, 'query'),
      },
      mutations: {
        throwOnError: (error) => shouldThrowGlobally(error, 'mutation'),
      },
    },
    queryCache: new QueryCache({}),
  })
  const options: VueQueryPluginOptions = { queryClient, enableDevtoolsV6Plugin: true }

  nuxt.vueApp.use(VueQueryPlugin, options)

  if (import.meta.server) {
    nuxt.hooks.hook('app:rendered', () => {
      vueQueryState.value = dehydrate(queryClient)
    })
  }

  if (import.meta.client) {
    nuxt.hooks.hook('app:created', () => {
      hydrate(queryClient, vueQueryState.value)
    })
  }
})
